import { useCallback, useMemo } from 'react'
import type { CanvasPosition, EndpointDisplayInfo, MapStyle } from '../../types/infrastructure-map'
import type { CaseEndpoint } from '../../types'
import type { EndpointRelationship } from '../../types/case-container/relationships'
import {
  buildBusTopologyData,
  buildLinksToRender,
  buildNodesToRender,
  buildOrphanEndpoints,
  buildTreeTopologyData,
  computeBusCompressedPositions,
  computeCollapsedPositions,
} from './infrastructure-map-canvas.helpers'

interface UseCanvasDerivedArgs {
  endpoints: CaseEndpoint[]
  relationships: EndpointRelationship[]
  parentChildMap: Map<string, string[]>
  dragPositions: Map<string, CanvasPosition>
  infraMap: {
    summaryTiles: Map<
      string,
      {
        parentEndpointId: string
        directChildCount: number
        totalDescendantCount: number
        isRecursive: boolean
        hiddenEndpointIds: string[]
      }
    >
    displayInfo: Map<string, EndpointDisplayInfo>
    mapStyle: MapStyle
    orphanIds: string[]
  }
}

export interface CanvasDerivedData {
  endpointLookup: Map<string, CaseEndpoint>
  getEndpointById: (id: string) => CaseEndpoint | undefined
  collapsedPositions: Map<string, CanvasPosition>
  busCompressedPositions: Map<string, CanvasPosition>
  nodesToRender: ReturnType<typeof buildNodesToRender>
  orphanEndpoints: ReturnType<typeof buildOrphanEndpoints>
  busTopologyData: ReturnType<typeof buildBusTopologyData>
  treeTopologyData: ReturnType<typeof buildTreeTopologyData>
  linksToRender: ReturnType<typeof buildLinksToRender>
}

interface EndpointLookupResult {
  endpointLookup: Map<string, CaseEndpoint>
  getEndpointById: (id: string) => CaseEndpoint | undefined
}

function useEndpointLookup(endpoints: CaseEndpoint[]): EndpointLookupResult {
  const endpointLookup = useMemo(() => {
    const map = new Map<string, CaseEndpoint>()
    for (const ep of endpoints) map.set(ep.id, ep)
    return map
  }, [endpoints])

  const getEndpointById = useCallback((id: string) => endpointLookup.get(id), [endpointLookup])

  return { endpointLookup, getEndpointById }
}

interface LinkRenderingArgs {
  dragPositions: Map<string, CanvasPosition>
  infraMap: { displayInfo: Map<string, EndpointDisplayInfo> }
  nodesToRender: ReturnType<typeof buildNodesToRender>
  relationships: EndpointRelationship[]
}

function useLinkRenderingData({
  dragPositions,
  infraMap,
  nodesToRender,
  relationships,
}: LinkRenderingArgs): { linksToRender: ReturnType<typeof buildLinksToRender> } {
  const getEffectivePosition = useCallback(
    (endpointId: string): CanvasPosition | null => {
      const dragPos = dragPositions.get(endpointId)
      if (dragPos) return dragPos
      const info = infraMap.displayInfo.get(endpointId)
      if (info?.position) return info.position
      return nodesToRender.find((node) => node.endpointId === endpointId)?.position ?? null
    },
    [dragPositions, infraMap.displayInfo, nodesToRender]
  )

  const linksToRender = useMemo(
    () => buildLinksToRender(relationships, getEffectivePosition),
    [relationships, getEffectivePosition]
  )

  return { linksToRender }
}

interface TopologyArgs {
  infraMap: { displayInfo: Map<string, EndpointDisplayInfo>; mapStyle: MapStyle }
  relationships: EndpointRelationship[]
  busCompressedPositions: Map<string, CanvasPosition>
  nodesToRender: ReturnType<typeof buildNodesToRender>
}

function useTopologyData({
  infraMap,
  relationships,
  busCompressedPositions,
  nodesToRender,
}: TopologyArgs): {
  busTopologyData: ReturnType<typeof buildBusTopologyData>
  treeTopologyData: ReturnType<typeof buildTreeTopologyData>
} {
  const busTopologyData = useMemo(
    () =>
      buildBusTopologyData({
        mapStyle: infraMap.mapStyle,
        displayInfo: infraMap.displayInfo,
        relationships,
        busCompressedPositions,
      }),
    [infraMap.mapStyle, infraMap.displayInfo, relationships, busCompressedPositions]
  )

  const treeTopologyData = useMemo(
    () =>
      buildTreeTopologyData({
        mapStyle: infraMap.mapStyle,
        displayInfo: infraMap.displayInfo,
        nodesToRender,
        relationships,
      }),
    [infraMap.mapStyle, infraMap.displayInfo, nodesToRender, relationships]
  )

  return { busTopologyData, treeTopologyData }
}

export function useCanvasDerived({
  endpoints,
  relationships,
  parentChildMap,
  dragPositions,
  infraMap,
}: UseCanvasDerivedArgs): CanvasDerivedData {
  const { endpointLookup, getEndpointById } = useEndpointLookup(endpoints)

  const collapsedPositions = useMemo(
    () => computeCollapsedPositions(infraMap.summaryTiles, infraMap.displayInfo, parentChildMap),
    [infraMap.summaryTiles, infraMap.displayInfo, parentChildMap]
  )

  const busCompressedPositions = useMemo(
    () => computeBusCompressedPositions(infraMap.mapStyle, infraMap.displayInfo, relationships),
    [infraMap.mapStyle, infraMap.displayInfo, relationships]
  )

  const nodesToRender = useMemo(
    () =>
      buildNodesToRender(
        {
          displayInfo: infraMap.displayInfo,
          mapStyle: infraMap.mapStyle,
          dragPositions,
          collapsedPositions,
          busCompressedPositions,
        },
        relationships
      ),
    [
      infraMap.displayInfo,
      infraMap.mapStyle,
      dragPositions,
      collapsedPositions,
      busCompressedPositions,
      relationships,
    ]
  )

  const orphanEndpoints = useMemo(
    () => buildOrphanEndpoints(infraMap.orphanIds, endpointLookup),
    [infraMap.orphanIds, endpointLookup]
  )

  const { busTopologyData, treeTopologyData } = useTopologyData({
    infraMap,
    relationships,
    busCompressedPositions,
    nodesToRender,
  })

  const { linksToRender } = useLinkRenderingData({
    dragPositions,
    infraMap,
    nodesToRender,
    relationships,
  })

  return {
    endpointLookup,
    getEndpointById,
    collapsedPositions,
    busCompressedPositions,
    nodesToRender,
    orphanEndpoints,
    busTopologyData,
    treeTopologyData,
    linksToRender,
  }
}
